import React, { useRef, useEffect } from 'react';
import { useField, Formik } from 'formik';
import moment from 'moment-timezone';
import * as Yup from 'yup';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import HistoryIcon from '@mui/icons-material/History';
import { Box, Grid, Button } from '@mui/material';
import { FormInput } from './FormInput.js';
import { FormSelect } from './FormSelect.js';
import { TimeRange, SearchModeListDatetime } from '../../models/TimeRange.js';
import { SearchModeList, GetSearchMode } from '../../utils/query.js';

// libs/src/components/Form/SearchDetailFormInput.tsx
const SearchDetailFormInput = ({
  keys = [{
    label: 'ミッション名',
    value: 'name',
    enabled: false,
    type: 'string'
  }],
  name = '',
  sx,
  onSubmit,
  onDelete,
  onChange,
  onDatetimeChange,
  formik,
  ...props
}) => {
  const [field] = useField(name);
  const RenderForm = ({
    formik: _formik
  }) => {
    const mode = GetSearchMode(_formik.values.mode);
    const v1enabled = mode.nValues >= 1;
    const v2enabled = mode.nValues >= 2;
    const key = keys.filter(v => v.value === _formik.values.key);
    let type = 'string';
    if (key.length > 0) {
      type = key[0].type;
    }
    let ModeList = SearchModeList;
    if (type === 'datetime') {
      // 日時で検索する場合
      ModeList = SearchModeListDatetime;
      if (ModeList.filter(v => v.value === _formik.values.mode).length === 0) {
        _formik.setFieldValue('mode', 'WEEK_TEIKI');
        _formik.setFieldValue('value0', moment().utc().format('YYYY-MM-DDTHH:mm'));
      }
    }
    const formikRef = useRef(_formik);
    formikRef.current = _formik;
    // 有効化されたときのみ値を同期する
    useEffect(() => {
      if (formikRef.current.values.enabled) {
        formikRef.current.setValues(field.value);
      }
    }, [field.value]);
    return /*#__PURE__*/React.createElement("form", {
      onSubmit: _formik.handleSubmit
    }, /*#__PURE__*/React.createElement(Grid, {
      container: true,
      sx: sx
    }, /*#__PURE__*/React.createElement(Grid, {
      item: true,
      xs: v1enabled ? 6 : 5,
      sm: v1enabled ? 2 : 5,
      sx: {
        pl: 0.25
      }
    }, /*#__PURE__*/React.createElement(FormSelect, {
      formik: _formik,
      name: "key",
      title: "Key",
      options: keys,
      disabled: _formik.values.enabled
    })), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      xs: v1enabled ? 6 : 5,
      sm: v1enabled ? 2 : 5,
      sx: {
        pl: 0.25
      }
    }, /*#__PURE__*/React.createElement(FormSelect, {
      formik: _formik,
      name: "mode",
      title: "\u691C\u7D22\u30E2\u30FC\u30C9",
      options: ModeList.map(v => ({
        label: v.label || v.value,
        value: v.value
      })),
      onChange: async e => {
        let pld = {
          ..._formik.values,
          mode: e.target.value
        };
        if (type === 'datetime') {
          pld = {
            ...TimeRange.fromMode(String(pld.value0), pld.mode).toQueryItem(_formik.values.key),
            type: 'datetime',
            ...pld
          };
        }
        onChange && (await onChange(pld));
      }
    })), v1enabled && /*#__PURE__*/React.createElement(Grid, {
      item: true,
      xs: v2enabled || !_formik.values.enabled ? 12 : 9,
      sm: _formik.values.enabled ? v2enabled ? 3 : 6 : v2enabled ? 4 : 8,
      sx: {
        pl: 0.25
      }
    }, /*#__PURE__*/React.createElement(FormInput, {
      type: type,
      formik: _formik,
      name: "value0",
      title: "Value",
      onChange: async e => {
        onChange && (await onChange({
          ..._formik.values,
          value0: e.target.value,
          type
        }));
      }
    })), v2enabled && /*#__PURE__*/React.createElement(Grid, {
      item: true,
      xs: v2enabled ? 9 : 12,
      sm: _formik.values.enabled ? v2enabled ? 3 : 6 : v2enabled ? 4 : 8,
      sx: {
        pl: 0.25
      }
    }, /*#__PURE__*/React.createElement(FormInput, {
      type: type,
      formik: _formik,
      name: "value1",
      title: "Value (\u4E0A\u9650)",
      onChange: async e => {
        onChange && (await onChange({
          ..._formik.values,
          value1: e.target.value,
          type
        }));
      }
    })), _formik.values.enabled && /*#__PURE__*/React.createElement(Grid, {
      item: true,
      xs: 3,
      sm: 2,
      sx: {
        pl: 0.25,
        pb: 0.25
      }
    }, /*#__PURE__*/React.createElement(Button, {
      variant: "outlined",
      color: "error",
      onClick: async () => {
        onDelete && (await onDelete(_formik.values));
      },
      sx: {
        width: '100%',
        height: '100%'
      }
    }, /*#__PURE__*/React.createElement(DeleteOutlineIcon, {
      fontSize: "small"
    }), "\u524A\u9664")), type === 'datetime' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Grid, {
      item: true,
      xs: 3,
      sm: 2,
      sx: {
        pl: 0.25
      }
    }, /*#__PURE__*/React.createElement(Button, {
      variant: "outlined",
      color: "primary",
      sx: {
        width: '100%'
      },
      onClick: async () => {
        let pld = TimeRange.fromMode(_formik.values.value0, _formik.values.mode).prev();
        _formik.setFieldValue('type', type);
        _formik.setFieldValue('value0', pld.start);
        _formik.setFieldValue('mode', pld.mode);
        onDatetimeChange && (await onDatetimeChange({
          ..._formik.values,
          value0: pld.start,
          mode: pld.mode,
          type: 'datetime'
        }));
      }
    }, /*#__PURE__*/React.createElement(NavigateBeforeIcon, {
      fontSize: "small"
    }), "\u524D")), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      xs: 6,
      sm: 8,
      sx: {
        pl: 0.25
      }
    }, /*#__PURE__*/React.createElement(Button, {
      variant: "outlined",
      color: "info",
      sx: {
        width: '100%'
      },
      onClick: async () => {
        let pld = TimeRange.fromMode(_formik.values.value0, _formik.values.mode).now();
        _formik.setFieldValue('type', type);
        _formik.setFieldValue('value0', pld.start);
        _formik.setFieldValue('mode', pld.mode);
        onDatetimeChange && (await onDatetimeChange({
          ..._formik.values,
          value0: pld.start,
          mode: pld.mode,
          type: 'datetime'
        }));
      }
    }, /*#__PURE__*/React.createElement(HistoryIcon, {
      fontSize: "small"
    }), "\u73FE\u5728\u6642\u523B\u3067\u691C\u7D22")), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      xs: 3,
      sm: 2,
      sx: {
        pl: 0.25
      }
    }, /*#__PURE__*/React.createElement(Button, {
      variant: "outlined",
      color: "error",
      sx: {
        width: '100%',
        height: '100%'
      },
      onClick: async () => {
        let pld = TimeRange.fromMode(_formik.values.value0, _formik.values.mode).next();
        _formik.setFieldValue('type', type);
        _formik.setFieldValue('value0', pld.start);
        _formik.setFieldValue('mode', pld.mode);
        onDatetimeChange && (await onDatetimeChange({
          ..._formik.values,
          value0: pld.start,
          mode: pld.mode,
          type: 'datetime'
        }));
      }
    }, "\u6B21", /*#__PURE__*/React.createElement(NavigateNextIcon, {
      fontSize: "small"
    })))), !_formik.values.enabled && /*#__PURE__*/React.createElement(Grid, {
      item: true,
      xs: 12,
      sx: {
        pl: 0.25
      }
    }, /*#__PURE__*/React.createElement(Button, {
      variant: "outlined",
      color: "primary",
      onClick: () => {
        _formik.setFieldValue('type', type);
        _formik.handleSubmit(_formik.values);
      },
      sx: {
        width: '100%',
        height: '100%',
        py: 1.5
      }
    }, /*#__PURE__*/React.createElement(AddIcon, {
      fontSize: "small"
    }), "\u691C\u7D22\u6761\u4EF6\u3092\u8FFD\u52A0"))));
  };
  return /*#__PURE__*/React.createElement(Box, props, /*#__PURE__*/React.createElement(Formik, {
    initialValues: field.value || keys.length && {
      key: keys[0].value,
      mode: SearchModeList[0].value,
      enabled: false,
      type: 'string'
    },
    validationSchema: Yup.object().shape({
      key: Yup.string().required('検索キーを入力してください'),
      mode: Yup.string().required('検索モードを入力してください'),
      value0: Yup.string().when('mode', {
        is: mode => GetSearchMode(mode).nValues >= 1,
        then: Yup.string().required('値を入力してください')
      }),
      value1: Yup.string().when('mode', {
        is: mode => GetSearchMode(mode).nValues >= 2,
        then: Yup.string().required('値 (上限) を入力してください')
      })
    }),
    onSubmit: async (values, {
      setSubmitting,
      resetForm
    }) => {
      setSubmitting(false);
      onSubmit && (await onSubmit({
        ...values,
        enabled: true
      }));
      resetForm({
        values: {
          key: keys.filter(v => v.value !== values.key)[0].value,
          type: keys[0].type,
          mode: values.mode,
          enabled: false
        }
      });
    }
  }, f => /*#__PURE__*/React.createElement(RenderForm, {
    formik: f
  })));
};

export { SearchDetailFormInput };
